import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { styled } from 'styled-components';
import { Button, Icon, type IconGlyph, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components';
import { SunwebLogoCompact } from '@/scopes/common/components/Header/Logo';
import { useUser } from '@/domain/user/hooks';
import { useFavouriteProductIdsQuery } from '@/domain/product/hooks';
import { useIntl } from '@/intl';
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createAccountHeaderClickEvent } from '@/domain/header/analytics';
import { Dropdown } from './Dropdown';

interface MenuItemProps {
  href?: string;
  onClick?: () => void;
  icon?: IconGlyph;
  labelId: string;
  rightText?: number;
}

const AccountMenuItem: React.FC<MenuItemProps> = ({ href, onClick, icon, labelId, rightText }) => {
  const intl = useIntl();

  return (
    <AccountMenu.DropdownMenuItem>
      {href ? (
        <AccountMenu.DropdownMenuLink href={href} onClick={onClick}>
          <SpaceBetween direction="x" spacing={tk('spacing_2xs')} alignItems="center" justifyContent="space-between">
            <SpaceBetween direction="x" spacing={tk('spacing_2xs')} alignItems="center">
              {icon && <Icon glyph={icon} size="sm" />}
              <Typography>{intl.formatMessage({ id: labelId })}</Typography>
            </SpaceBetween>
            {rightText != null && <Typography $variant="body-md-bold">{rightText}</Typography>}
          </SpaceBetween>
        </AccountMenu.DropdownMenuLink>
      ) : (
        <AccountMenu.DropdownMenuButton onClick={onClick}>
          <Typography>{intl.formatMessage({ id: labelId })}</Typography>
        </AccountMenu.DropdownMenuButton>
      )}
    </AccountMenu.DropdownMenuItem>
  );
};

const LoggedOutMenu: React.FC = () => {
  const intl = useIntl();
  const links = useConfiguration('links');
  const { sendEvent } = useAnalytics();
  const { isSunwebCE, logout } = useUser();
  const { data: favourites } = useFavouriteProductIdsQuery();
  const track = (result: string, description: string) => () => {
    sendEvent(createAccountHeaderClickEvent({ result, description }));
  };

  return (
    <>
      <AccountMenu.UpperLogoutContainer>
        <SunwebLogoCompact />
        <AccountMenu.TitleUpperLogout $variant="heading-md">
          {intl.formatMessage({ id: 'header_dropdown_menu_title' })}
        </AccountMenu.TitleUpperLogout>
        <AccountMenu.LoginButton
          as="a"
          href={links.login}
          $fullSize
          onClick={track('Click on My Sunweb', 'redirects to the login page')}
        >
          {intl.formatMessage({ id: 'header_dropdown_menu_login' })}
        </AccountMenu.LoginButton>
        <AccountMenu.RegisterLink
          href={links.createAccount}
          onClick={track('Click on Create Account', 'redirects to the account creation page')}
        >
          <Typography>{intl.formatMessage({ id: 'header_dropdown_menu_create_account' })}</Typography>
        </AccountMenu.RegisterLink>
      </AccountMenu.UpperLogoutContainer>

      <AccountMenu.DropdownMenuDivider />
      {!isSunwebCE && (
        <>
          <AccountMenuItem
            href={links.myPortal}
            icon="IconSuitcase"
            labelId="header_my_bookings"
            onClick={track('Click on My Bookings', "redirects to 'My Sunweb' page")}
          />
          <AccountMenuItem
            href={links.favourites}
            icon="IconHeart"
            labelId="header_my_favourites"
            rightText={favourites?.length ?? 0}
            onClick={track('Click on Favorites', 'redirects to the favorites page')}
          />
        </>
      )}
      {links.loginCE &&
        (isSunwebCE ? (
          <AccountMenuItem onClick={logout} labelId="header_dropdown_menu_logout_ce" />
        ) : (
          <AccountMenuItem href={links.loginCE} icon="IconVoucher" labelId="header_login_ce" />
        ))}
    </>
  );
};

const LoggedInMenu: React.FC = () => {
  const links = useConfiguration('links');
  const { data: favourites } = useFavouriteProductIdsQuery();
  const { logout } = useUser();
  const items: MenuItemProps[] = [
    { href: links.myPortal, icon: 'IconSuitcase', labelId: 'header_my_bookings' },
    { href: links.favourites, icon: 'IconHeart', labelId: 'header_my_favourites', rightText: favourites?.length },
  ];
  if (links.loginCE) {
    items.push({ href: links.loginCE, icon: 'IconVoucher', labelId: 'header_login_ce' });
  }

  return (
    <>
      {items.map((item, i) => (
        <AccountMenuItem key={i} {...item} />
      ))}
      <AccountMenu.DropdownMenuDivider />
      <AccountMenuItem href={links.myProfile} labelId="header_my_profile" />
      <AccountMenu.DropdownMenuDivider />
      <AccountMenuItem onClick={logout} labelId="header_dropdown_menu_logout" />
    </>
  );
};

export function AccountMenu() {
  const intl = useIntl();
  const links = useConfiguration('links');
  const { capitalLetter, isLoggedIn, isSunwebCE } = useUser();
  const showLoggedIn = isLoggedIn && (!links.loginCE || !isSunwebCE);

  const trigger =
    isLoggedIn && capitalLetter ? (
      <AccountMenu.Avatar>{capitalLetter}</AccountMenu.Avatar>
    ) : (
      <Icon glyph="IconOnePersonCircle" size="md" />
    );

  return (
    <Dropdown.Menu>
      <Dropdown.Trigger>
        {trigger}
        <Typography>{intl.formatMessage({ id: 'my_portal' })}</Typography>
      </Dropdown.Trigger>
      <AccountMenu.DropdownMenuContent align="end" sideOffset={5}>
        {showLoggedIn ? <LoggedInMenu /> : <LoggedOutMenu />}
      </AccountMenu.DropdownMenuContent>
    </Dropdown.Menu>
  );
}

AccountMenu.RegisterLink = styled.a`
  color: inherit;
  margin-top: ${tk('spacing_sm')};
  min-height: 0;
  padding: 0;
  text-decoration: underline;

  ${Typography} {
    font-weight: ${tk('font_weight_semibold')};
  }
`;

AccountMenu.Avatar = styled.div`
  ${Typography.modVariant('body-md')};
  align-items: center;
  background: ${tk('color_semantic_icon')};
  border-radius: 50%;
  color: ${tk('color_semantic_text_on_color')};
  display: flex;
  height: ${tk('spacing_lg')};
  justify-content: center;
  width: ${tk('spacing_lg')};
`;

AccountMenu.DropdownMenuContent = styled(DropdownMenu.Content)`
  align-items: center;
  background: ${tk('color_semantic_bg')};
  border-radius: ${tk('radius_large')};
  box-shadow: ${tk('shadow_lg_top')};
  display: flex;
  flex-direction: column;
  padding: ${tk('spacing_md')} 0;
  width: 300px;
  z-index: 1;

  > ${Typography} {
    margin-top: ${tk('spacing_xs')};
    margin-bottom: ${tk('spacing_md')};
    text-align: center;
  }
`;

AccountMenu.DropdownMenuLink = styled.a`
  color: currentColor;
  display: block;
  padding: ${tk('spacing_sm')} ${tk('spacing_lg')};
  text-decoration: none;

  &:hover {
    background: ${tk('color_semantic_bg_subdued_hover')};
    color: currentColor;
  }
`;

AccountMenu.DropdownMenuButton = styled.button`
  background: none;
  border: none;
  color: currentColor;
  cursor: pointer;
  display: block;
  padding: ${tk('spacing_sm')} ${tk('spacing_lg')};
  text-align: left;
  width: 100%;

  &:hover {
    background: ${tk('color_semantic_bg_subdued_hover')};
  }
`;

AccountMenu.DropdownMenuItem = styled(DropdownMenu.Item)`
  width: 100%;
`;

AccountMenu.DropdownMenuDivider = styled(DropdownMenu.Separator)`
  background: ${tk('color_semantic_bg_subdued_hover')};
  height: 2px;
  margin: ${tk('spacing_md')} 0;
  width: calc(100% - (${tk('spacing_lg')} * 2));
`;

AccountMenu.LoginButton = styled(Button)`
  margin: 0 auto;
  width: 100%;

  &:hover {
    // Needed to override the Button hover styles in legacy DS, once fully migrated to React, this can be removed
    color: ${tk('color_semantic_text_on_color')};
  }
`;

AccountMenu.UpperLogoutContainer = styled.div`
  margin: 0 ${tk('spacing_lg')};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

AccountMenu.TitleUpperLogout = styled(Typography)`
  margin-bottom: ${tk('spacing_sm')};
  margin-top: ${tk('spacing_xs')};
`;
